import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  PersonOutline as PersonIcon,
  PersonAddOutlined as PersonAddIcon,
  AssignmentOutlined as OrderIcon,
  ImageOutlined as ImageIcon,
  Inventory2Outlined as ArchiveIcon,
  DescriptionOutlined as ReportIcon,
  LockOutlined as LockIcon,
  LogoutOutlined as LogoutIcon,
  BiotechOutlined as LabSampleIcon,
  HubOutlined as AnalyzerIcon,
  AssessmentOutlined as LabResultIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import FullLogo from '../logo/FullLogo';

// Custom SVG Icon for DycoRIS
const RisHeaderIcon = () => (
  <Box
    sx={{
      position: 'relative',
      width: 72,
      height: 72,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: '#E6F4F1',
      borderRadius: '16px',
      flexShrink: 0,
      boxShadow: '0 4px 12px rgba(0, 105, 92, 0.05)',
    }}
  >
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#00796B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="4" width="14" height="16" rx="2" strokeWidth="2" />
      <path d="M9 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1" fill="#00796B" />
      <circle cx="12" cy="11" r="2" strokeWidth="2" />
      <path d="M8 17c0-1.5 1.5-2.5 4-2.5s4 1 4 2.5" strokeWidth="2" />
    </svg>
    <Box
      sx={{
        position: 'absolute',
        bottom: 8,
        right: 8,
        width: 18,
        height: 18,
        borderRadius: '50%',
        bgcolor: '#004D40',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px solid #E6F4F1',
      }}
    >
      <Typography sx={{ color: 'white', fontSize: '11px', fontWeight: 'bold', lineHeight: 1 }}>+</Typography>
    </Box>
  </Box>
);

// Custom SVG Icon for DycoPACS
const PacsHeaderIcon = () => (
  <Box
    sx={{
      width: 72,
      height: 72,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: '#E3F2FD',
      borderRadius: '16px',
      flexShrink: 0,
      boxShadow: '0 4px 12px rgba(13, 71, 161, 0.05)',
    }}
  >
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7 20H17" stroke="#0D47A1" strokeWidth="2.2" strokeLinecap="round" />
      <path d="M12 15V20" stroke="#0D47A1" strokeWidth="2.2" strokeLinecap="round" />
      <rect x="3" y="3" width="18" height="12" rx="2" fill="#0D47A1" />
      <rect x="4.5" y="4.5" width="15" height="9" rx="1" fill="#0A3C8F" />
      <line x1="12" y1="5.5" x2="12" y2="12.5" stroke="#90CAF9" strokeWidth="1" strokeDasharray="1 1" />
      <path d="M10 6.5C8.8 6.5 8 7.3 8 8.1" stroke="#E3F2FD" strokeWidth="1" strokeLinecap="round" />
      <path d="M10.5 8.2C8.8 8.2 7.5 9 7.5 9.8" stroke="#E3F2FD" strokeWidth="1" strokeLinecap="round" />
      <path d="M10.5 9.8C8.8 9.8 7.5 10.6 7.5 11" stroke="#E3F2FD" strokeWidth="1" strokeLinecap="round" />
      <path d="M14 6.5C15.2 6.5 16 7.3 16 8.1" stroke="#E3F2FD" strokeWidth="1" strokeLinecap="round" />
      <path d="M13.5 8.2C15.2 8.2 16.5 9 16.5 9.8" stroke="#E3F2FD" strokeWidth="1" strokeLinecap="round" />
      <path d="M13.5 9.8C15.2 9.8 16.5 10.6 16.5 11" stroke="#E3F2FD" strokeWidth="1" strokeLinecap="round" />
    </svg>
  </Box>
);

// Custom SVG Icon for DycoLIS
const LisHeaderIcon = () => (
  <Box
    sx={{
      position: 'relative',
      width: 72,
      height: 72,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: '#F3E8FF',
      borderRadius: '16px',
      flexShrink: 0,
      boxShadow: '0 4px 12px rgba(124, 58, 237, 0.05)',
    }}
  >
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 2v7.31a2 2 0 0 1-.5 1.34L3.6 17.5A2 2 0 0 0 5.14 21H18.86a2 2 0 0 0 1.54-3.5l-5.9-6.85A2 2 0 0 1 14 9.31V2" />
      <path d="M8.5 2h7" />
      <path d="M7 16h10" />
      <circle cx="10" cy="18.5" r=".7" fill="#7C3AED" />
      <circle cx="14" cy="18.5" r=".7" fill="#7C3AED" />
    </svg>
    <Box
      sx={{
        position: 'absolute',
        bottom: 8,
        right: 8,
        width: 18,
        height: 18,
        borderRadius: '50%',
        bgcolor: '#6D28D9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px solid #F3E8FF',
      }}
    >
      <Typography sx={{ color: 'white', fontSize: '11px', fontWeight: 'bold', lineHeight: 1 }}>+</Typography>
    </Box>
  </Box>
);

const FeatureItem = ({ icon, title, description, variant = 'ris' }) => {
  let iconBg = '#E6F4F1';
  let iconColor = '#00695C';

  if (variant === 'pacs') {
    iconBg = '#E3F2FD';
    iconColor = '#0D47A1';
  } else if (variant === 'lis') {
    iconBg = '#F3E8FF';
    iconColor = '#7C3AED';
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5, textAlign: 'left' }}>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '12px',
          bgcolor: iconBg,
          color: iconColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 2.5,
          flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 22 } })}
      </Box>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B', fontSize: '0.925rem', lineHeight: 1.2 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.825rem', mt: 0.25 }}>
          {description}
        </Typography>
      </Box>
    </Box>
  );
};

export default function PortalChoice() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Auto-redirect roles that have a dedicated dashboard (bypass portal choice)
  useEffect(() => {
    if (!user) return;
    if (user.role === 'radiologist') {
      navigate(user.radiologistType !== 'facility' ? '/radiologist/reader-portal' : '/radiologist');
      return;
    }
    const dedicatedDashboardRoles = {
      technologist: '/technologist',
      radiographer: '/technologist',
      admin: '/admin',
      tech_support: '/admin',
      product_lead: '/admin',
      customer_success: '/admin',
      facility_admin: '/facility-admin',
      it_admin: '/it-admin',
      compliance_officer: '/compliance',
      compliance_internal: '/compliance',
    };
    const clinicalOnlyRoles = ['referring_physician', 'nurse', 'medical_records_officer', 'sonographer'];
    if (dedicatedDashboardRoles[user.role]) {
      navigate(dedicatedDashboardRoles[user.role]);
    } else if (clinicalOnlyRoles.includes(user.role)) {
      navigate('/clinician');
    }
  }, [user, navigate]);

  const RIS_ROLES = ['clinician', 'super_clinician', 'radiologist', 'admin', 'referring_physician', 'nurse', 'medical_records_officer', 'sonographer', 'radiographer'];
  const PACS_ROLES = ['radiologist', 'admin', 'clinician', 'super_clinician', 'sonographer'];
  const LIS_ROLES = ['clinician', 'super_clinician', 'radiologist', 'admin', 'lab_technician', 'lab_director', 'pathologist', 'nurse', 'medical_records_officer'];

  const hasRisAccess = RIS_ROLES.includes(user?.role);
  const hasPacsAccess = PACS_ROLES.includes(user?.role);
  const hasLisAccess = LIS_ROLES.includes(user?.role) || true; // Accessible to clinic staff and lab operators

  const handleEnterRis = () => {
    if (hasRisAccess) {
      navigate('/clinician');
    }
  };

  const handleEnterPacs = () => {
    if (hasPacsAccess) {
      navigate('/radiologist');
    }
  };

  const handleEnterLis = () => {
    if (hasLisAccess) {
      navigate('/laboratory');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#F8FAFC',
        color: '#1E293B',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        px: 2,
        position: 'relative',
      }}
    >
      {/* Top-right sign out */}
      <Box sx={{ position: 'absolute', top: 20, right: 24, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.85rem' }}>
          {user?.firstName} {user?.lastName}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<LogoutIcon />}
          onClick={logout}
          sx={{
            borderColor: '#E2E8F0',
            color: '#64748B',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8rem',
            borderRadius: '10px',
            '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F1F5F9', color: '#1E293B' },
          }}
        >
          Sign Out
        </Button>
      </Box>

      {/* Brand Logo */}
      <Box sx={{ mb: 5, transform: 'scale(1.15)', display: 'flex', justifyContent: 'center' }}>
        <FullLogo />
      </Box>

      <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 } }}>
        <Grid container spacing={3.5} justifyContent="center" alignItems="stretch">
          
          {/* DycoRIS Card */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '24px',
                bgcolor: '#FFFFFF',
                color: '#1E293B',
                border: '1px solid #E2E8F0',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.02)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                position: 'relative',
                overflow: 'visible',
                opacity: hasRisAccess ? 1 : 0.85,
                '&:hover': hasRisAccess ? {
                  transform: 'translateY(-6px)',
                  boxShadow: '0 20px 40px rgba(0, 105, 92, 0.08)',
                  borderColor: '#00695C',
                } : {},
              }}
            >
              {!hasRisAccess && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -14,
                    right: 24,
                    bgcolor: '#F1F5F9',
                    color: '#64748B',
                    px: 2,
                    py: 0.75,
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    border: '1.5px solid #FFFFFF',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                    zIndex: 2,
                  }}
                >
                  <LockIcon sx={{ fontSize: 13, color: '#64748B' }} />
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#64748B', letterSpacing: '0.02em' }}>Restricted</Typography>
                </Box>
              )}
              
              <CardContent sx={{ p: 3.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Header */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <RisHeaderIcon />
                    <Box sx={{ textAlign: 'left', flex: 1, minWidth: 0 }}>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#00695C', fontSize: '1.65rem', lineHeight: 1.1 }}>
                        DycoRIS
                      </Typography>
                      <Box sx={{ display: 'inline-block', mt: 0.5 }}>
                        <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 600, fontSize: '0.825rem', lineHeight: 1.3 }}>
                          Radiology Information System
                        </Typography>
                        <Box
                          sx={{
                            width: '100%',
                            height: 3,
                            borderRadius: '2px',
                            bgcolor: '#00695C',
                            mt: 0.75,
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Short Description */}
                <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.85rem', textAlign: 'left', mb: 2, lineHeight: 1.45, minHeight: '40px' }}>
                  Manage patients, register and create imaging orders.
                </Typography>

                {/* Divider Line */}
                <Divider sx={{ mb: 2.5, borderColor: '#F1F5F9' }} />

                {/* Feature List */}
                <Box sx={{ flexGrow: 1, mb: 3.5 }}>
                  <FeatureItem
                    icon={<PersonIcon />}
                    title="Dashboard"
                    description="Overview of orders and patients"
                    variant="ris"
                  />
                  <FeatureItem
                    icon={<PersonAddIcon />}
                    title="Register Patient"
                    description="Add new or existing patient"
                    variant="ris"
                  />
                  <FeatureItem
                    icon={<OrderIcon />}
                    title="Create Order"
                    description="Create imaging request / order"
                    variant="ris"
                  />
                </Box>

                {/* Access Button */}
                <Tooltip title={!hasRisAccess ? 'Your user account does not have clinician/admin privileges.' : ''} arrow placement="top">
                  <span>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleEnterRis}
                      disabled={!hasRisAccess}
                      endIcon={<span>&rarr;</span>}
                      sx={{
                        bgcolor: '#00695C',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        textTransform: 'none',
                        py: 1.5,
                        borderRadius: '14px',
                        boxShadow: 'none',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: '#004D40',
                          boxShadow: '0 4px 12px rgba(0, 105, 92, 0.15)',
                          transform: 'translateY(-1px)',
                        },
                        '&:disabled': {
                          bgcolor: '#F1F5F9',
                          color: '#94A3B8',
                        },
                      }}
                    >
                      Enter DycoRIS
                    </Button>
                  </span>
                </Tooltip>
              </CardContent>
            </Card>
          </Grid>

          {/* DycoPACS Card */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '24px',
                bgcolor: '#FFFFFF',
                color: '#1E293B',
                border: '1px solid #E2E8F0',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.02)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                position: 'relative',
                overflow: 'visible',
                opacity: hasPacsAccess ? 1 : 0.85,
                '&:hover': hasPacsAccess ? {
                  transform: 'translateY(-6px)',
                  boxShadow: '0 20px 40px rgba(13, 71, 161, 0.08)',
                  borderColor: '#0D47A1',
                } : {},
              }}
            >
              {!hasPacsAccess && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -14,
                    right: 24,
                    bgcolor: '#F1F5F9',
                    color: '#64748B',
                    px: 2,
                    py: 0.75,
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    border: '1.5px solid #FFFFFF',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                    zIndex: 2,
                  }}
                >
                  <LockIcon sx={{ fontSize: 13, color: '#64748B' }} />
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#64748B', letterSpacing: '0.02em' }}>Restricted</Typography>
                </Box>
              )}

              <CardContent sx={{ p: 3.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Header */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <PacsHeaderIcon />
                    <Box sx={{ textAlign: 'left', flex: 1, minWidth: 0 }}>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#0D47A1', fontSize: '1.65rem', lineHeight: 1.1 }}>
                        DycoPACS
                      </Typography>
                      <Box sx={{ display: 'inline-block', mt: 0.5 }}>
                        <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 600, fontSize: '0.825rem', lineHeight: 1.3 }}>
                          Picture Archiving & Communication System
                        </Typography>
                        <Box
                          sx={{
                            width: '100%',
                            height: 3,
                            borderRadius: '2px',
                            bgcolor: '#0D47A1',
                            mt: 0.75,
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Short Description */}
                <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.85rem', textAlign: 'left', mb: 2, lineHeight: 1.45, minHeight: '40px' }}>
                  View images, manage studies and create reports.
                </Typography>

                {/* Divider Line */}
                <Divider sx={{ mb: 2.5, borderColor: '#F1F5F9' }} />

                {/* Feature List */}
                <Box sx={{ flexGrow: 1, mb: 3.5 }}>
                  <FeatureItem
                    icon={<ImageIcon />}
                    title="Image Viewer"
                    description="View and analyze imaging studies"
                    variant="pacs"
                  />
                  <FeatureItem
                    icon={<ArchiveIcon />}
                    title="Study Archive"
                    description="Search and manage archived studies"
                    variant="pacs"
                  />
                  <FeatureItem
                    icon={<ReportIcon />}
                    title="Reporting"
                    description="Create and manage reports"
                    variant="pacs"
                  />
                </Box>

                {/* Access Button */}
                <Tooltip title={!hasPacsAccess ? 'Your user account does not have radiologist/admin privileges.' : ''} arrow placement="top">
                  <span>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleEnterPacs}
                      disabled={!hasPacsAccess}
                      endIcon={<span>&rarr;</span>}
                      sx={{
                        bgcolor: '#0D47A1',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        textTransform: 'none',
                        py: 1.5,
                        borderRadius: '14px',
                        boxShadow: 'none',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: '#0B3C8F',
                          boxShadow: '0 4px 12px rgba(13, 71, 161, 0.15)',
                          transform: 'translateY(-1px)',
                        },
                        '&:disabled': {
                          bgcolor: '#F1F5F9',
                          color: '#94A3B8',
                        },
                      }}
                    >
                      Enter DycoPACS
                    </Button>
                  </span>
                </Tooltip>
              </CardContent>
            </Card>
          </Grid>

          {/* DycoLIS Card */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '24px',
                bgcolor: '#FFFFFF',
                color: '#1E293B',
                border: '1px solid #E2E8F0',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.02)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                position: 'relative',
                overflow: 'visible',
                opacity: hasLisAccess ? 1 : 0.85,
                '&:hover': hasLisAccess ? {
                  transform: 'translateY(-6px)',
                  boxShadow: '0 20px 40px rgba(124, 58, 237, 0.08)',
                  borderColor: '#7C3AED',
                } : {},
              }}
            >
              {!hasLisAccess && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -14,
                    right: 24,
                    bgcolor: '#F1F5F9',
                    color: '#64748B',
                    px: 2,
                    py: 0.75,
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    border: '1.5px solid #FFFFFF',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                    zIndex: 2,
                  }}
                >
                  <LockIcon sx={{ fontSize: 13, color: '#64748B' }} />
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#64748B', letterSpacing: '0.02em' }}>Restricted</Typography>
                </Box>
              )}

              <CardContent sx={{ p: 3.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Header */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <LisHeaderIcon />
                    <Box sx={{ textAlign: 'left', flex: 1, minWidth: 0 }}>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#7C3AED', fontSize: '1.65rem', lineHeight: 1.1 }}>
                        DycoLIS
                      </Typography>
                      <Box sx={{ display: 'inline-block', mt: 0.5 }}>
                        <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 600, fontSize: '0.825rem', lineHeight: 1.3 }}>
                          Laboratory Information System
                        </Typography>
                        <Box
                          sx={{
                            width: '100%',
                            height: 3,
                            borderRadius: '2px',
                            bgcolor: '#7C3AED',
                            mt: 0.75,
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Short Description */}
                <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.85rem', textAlign: 'left', mb: 2, lineHeight: 1.45, minHeight: '40px' }}>
                  Manage lab orders, sample collection, and analyzer results.
                </Typography>

                {/* Divider Line */}
                <Divider sx={{ mb: 2.5, borderColor: '#F1F5F9' }} />

                {/* Feature List */}
                <Box sx={{ flexGrow: 1, mb: 3.5 }}>
                  <FeatureItem
                    icon={<LabSampleIcon />}
                    title="Sample Tracking"
                    description="Barcode scanning & collection queue"
                    variant="lis"
                  />
                  <FeatureItem
                    icon={<AnalyzerIcon />}
                    title="Analyzer Ingestion"
                    description="Direct ASTM/HL7 instrument interface"
                    variant="lis"
                  />
                  <FeatureItem
                    icon={<LabResultIcon />}
                    title="Results & Reporting"
                    description="Validate findings and release reports"
                    variant="lis"
                  />
                </Box>

                {/* Access Button */}
                <Tooltip title={!hasLisAccess ? 'Your user account does not have laboratory privileges.' : ''} arrow placement="top">
                  <span>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleEnterLis}
                      disabled={!hasLisAccess}
                      endIcon={<span>&rarr;</span>}
                      sx={{
                        bgcolor: '#7C3AED',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        textTransform: 'none',
                        py: 1.5,
                        borderRadius: '14px',
                        boxShadow: 'none',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: '#6D28D9',
                          boxShadow: '0 4px 12px rgba(124, 58, 237, 0.15)',
                          transform: 'translateY(-1px)',
                        },
                        '&:disabled': {
                          bgcolor: '#F1F5F9',
                          color: '#94A3B8',
                        },
                      }}
                    >
                      Enter DycoLIS
                    </Button>
                  </span>
                </Tooltip>
              </CardContent>
            </Card>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
}
